/**
 * AnnouncementStream — the full-screen stream of Boxes. This IS the chat: one
 * announcement per box, newest at the bottom. Wires the three box actions:
 *   reply    → focus the command bar carrying this box's conversation context
 *   download → fetch the artifact as-is and open it (no conversion)
 *   control  → TOGGLE the control panel (view ⇄ control) — the box's context
 *              (@ref** / what it used) + the live activity progress. NOT a modal:
 *              it docks beside the view so work stays visible while you inspect.
 *
 * Scoped by the breadcrumb: only boxes whose refs sit under `scopePath` show.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { User as UserIcon } from "lucide-react";
import { useUser as useClerkUser } from "@clerk/clerk-react";

import { Box } from "./Box";
import { inConversation, prioritizedAnnouncementsOf, useConversationWorkspace } from "./ConversationWorkspace";
import {
  announcementArtifactMachineIdOf,
  artifactMachineRefOf,
  hasArtifactMachine,
  payloadArtifactOf,
  renderArtifactOf,
} from "./artifactMachine";
import { artifactToFile, saveBlob } from "./artifactDownload";
import type { Announcement, BoxAction, Ref } from "./contract";
import { artifactLifecycleCancelAnnounce, artifactLifecycleShareAnnounce, type ArtifactInteractionKind } from "./artifactInteraction";
import { routeAgentIntent } from "./agentSurface";
import { useFeedStoreState } from "./feedStore";
import { isWaitingStatus } from "./feedWave";
import { routeTaskAskInteraction } from "./taskAsk";
import { serverIdOf } from "./contract";
import { toneForParticipant, toneVars, type Tone } from "./tones";
import type { ChannelApi } from "./useChannel";

/** Emit-to-command-bar bus for the reply action (the command bar listens). */
export const REPLY_EVENT = "channel:reply";
/** Emit-to-Chrome bus for openAuth: open IntegrationManager pre-targeted to a server. */
export const OPEN_AUTH_EVENT = "channel:openAuth";

function underScope(a: Announcement, scopePath: string): boolean {
  if (!scopePath) return true;
  return a.refs.some((r) => r.path === scopePath || r.path.startsWith(`${scopePath}/`));
}

type StreamFacet = {
  id: string;
  label: string;
  kind: "world" | "current" | "recipient";
  tone: Tone;
  ref?: Ref;
};

const WORLD_FACET_ID = "participant:world";
const CURRENT_FACET_ID = "participant:current";

const refIdentity = (ref: Ref): string => String(ref.path || ref.machine?.id || ref.machine?.name || ref.label || "");
const refLabel = (ref: Ref): string => String(ref.label || ref.machine?.label || ref.path || "recipient");
const currentMatchesRef = (ref: Ref, ids: string[]): boolean => {
  const haystack = [
    ref.path,
    ref.label,
    ref.machine?.id,
    ref.machine?.name,
    ref.machine?.label,
  ].filter(Boolean).map(String);
  return ids.some((id) => haystack.some((value) => value === id || value.includes(id)));
};

const isWorldAnnouncement = (a: Announcement): boolean => a.sender !== "human";
const isCurrentAnnouncement = (a: Announcement, ids: string[]): boolean => (
  a.sender === "human" || a.recipient.some((ref) => currentMatchesRef(ref, ids))
);

const participantFacetIds = (a: Announcement, currentIds: string[]): string[] => {
  const ids = new Set<string>();
  if (isWorldAnnouncement(a)) ids.add(WORLD_FACET_ID);
  if (isCurrentAnnouncement(a, currentIds)) ids.add(CURRENT_FACET_ID);
  a.recipient.forEach((ref) => {
    if (!currentMatchesRef(ref, currentIds)) {
      const id = refIdentity(ref);
      if (id) ids.add(`participant:recipient:${id}`);
    }
  });
  return Array.from(ids);
};

const primaryParticipantFacetId = (a: Announcement, currentIds: string[]): string => {
  if (isCurrentAnnouncement(a, currentIds)) return CURRENT_FACET_ID;
  const recipient = a.recipient.find((ref) => !currentMatchesRef(ref, currentIds));
  if (recipient) return `participant:recipient:${refIdentity(recipient)}`;
  return WORLD_FACET_ID;
};

const streamFacets = (items: Announcement[], currentIds: string[]): StreamFacet[] => {
  const facets: StreamFacet[] = [
    { id: WORLD_FACET_ID, label: "World", kind: "world", tone: toneForParticipant("world") },
    { id: CURRENT_FACET_ID, label: "You", kind: "current", tone: toneForParticipant("current") },
  ];
  const seen = new Set<string>(facets.map((facet) => facet.id));
  for (const item of items) {
    for (const ref of item.recipient) {
      if (currentMatchesRef(ref, currentIds)) continue;
      const identity = refIdentity(ref);
      if (!identity) continue;
      const id = `participant:recipient:${identity}`;
      if (seen.has(id)) continue;
      seen.add(id);
      facets.push({
        id,
        label: refLabel(ref),
        kind: "recipient",
        tone: toneForParticipant("recipient", facets.length),
        ref,
      });
    }
  }
  return facets;
};

const payloadText = (payload: unknown): string => {
  const p = payload as { evidence?: unknown[] } | null;
  const evidence = Array.isArray(p?.evidence) ? p.evidence : [];
  for (const ev of evidence) {
    const item = ev as { body?: { text?: unknown; message?: unknown; data?: { text?: unknown; message?: unknown; result?: unknown } } };
    for (const v of [
      item.body?.text,
      item.body?.message,
      item.body?.data?.text,
      item.body?.data?.message,
      item.body?.data?.result,
    ]) {
      if (typeof v === "string") return v;
    }
  }
  return "";
};

export const AnnouncementStream: React.FC<{ ch: ChannelApi; scopePath?: string; conversationId?: string }> = ({
  ch,
  scopePath = "",
  conversationId = "",
}) => {
  const { user: clerkUser } = useClerkUser();
  const workspace = useConversationWorkspace();
  const feed = useFeedStoreState();
  const stream = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const currentIds = useMemo(() => [ch.userId, clerkUser?.id].filter((id): id is string => !!id), [ch.userId, clerkUser?.id]);
  const scoped = ch.announcements.filter((a) => underScope(a, scopePath) && inConversation(a, conversationId));
  const facets = useMemo(() => streamFacets(scoped, currentIds), [currentIds, scoped]);
  const toneByFacet = useMemo(() => new Map(facets.map((facet) => [facet.id, facet.tone])), [facets]);
  const visible = workspace.participantFilter
    ? scoped.filter((a) => participantFacetIds(a, currentIds).includes(workspace.participantFilter))
    : scoped;
  // Work status is the ONE waiting owner. `start`/`retry` are pending;
  // complete/fail:* are terminal. An empty current conversation has no status,
  // so it is an empty middle — never an invented loading state.
  const conversationWaiting = !!conversationId && feed.work.some((record) =>
    record.conversationId === conversationId && isWaitingStatus(record.status));
  const conversationError = conversationId ? workspace.workspaceError : "";
  const prioritizedArtifactIds = useMemo(() => new Set(
    prioritizedAnnouncementsOf(ch.announcements)
      .map((a) => announcementArtifactMachineIdOf(a))
      .filter(Boolean),
  ), [ch.announcements]);
  // (the stray artifactToast died into the ONE store crossing —
  // feedStore.foldLiveAnnouncement owns EVERY toast; no per-component toast
  // logic anywhere else.)

  // A conversation opens at its report, never underneath it. History backfill
  // preserves that position; only a genuinely NEW live box moves to the end.
  const streamKey = `${scopePath}|${conversationId}`;
  const positionedFor = useRef("");
  const seenStreamIds = useRef<Set<string>>(new Set());
  useLayoutEffect(() => {
    if (positionedFor.current !== streamKey) {
      positionedFor.current = streamKey;
      seenStreamIds.current = new Set(visible.map((a) => a.id));
      if (stream.current) stream.current.scrollTop = 0;
      return;
    }
    const fresh = visible.filter((a) => !seenStreamIds.current.has(a.id));
    if (!fresh.length) return;
    for (const a of fresh) seenStreamIds.current.add(a.id);
    const live = fresh.some((a) => ch.isLiveAnnouncement(a.id));
    if (live) bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [ch, streamKey, visible]);

  const onAction = async (action: BoxAction, a: Announcement) => {
    if (action === "opened" || action === "hovered" || action === "clicked" || action === "selected") {
      const artifact = payloadArtifactOf(a);
      if (hasArtifactMachine(artifact)) {
        void ch.trackArtifactInteraction({ artifact, announcement: a, interaction: action }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
      return;
    }
    if (action === "openAuth") {
      const artifact = payloadArtifactOf(a);
      if (hasArtifactMachine(artifact)) {
        void ch.trackArtifactInteraction({ artifact, announcement: a, interaction: "@auth" }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
      // an @auth announcement → open the auth modal for THIS server, SKIPPING the catalog.
      // Chrome owns the IntegrationManager; it listens for this and pre-targets the server.
      window.dispatchEvent(new CustomEvent(OPEN_AUTH_EVENT, { detail: { serverId: serverIdOf(a) } }));
      return;
    }
    if (action === "reply") {
      // reply TO this announcement → its OWN id is the reply-ref (the reply target,
      // oracle announcement:reply:@announcement**). Carry a LABEL so the CommandBar can SHOW what
      // you're replying to (it stacks them — reverse = click order).
      const txt = (a.render.find((rf) => rf.payload)?.payload as { text?: string } | undefined)?.text
        || payloadText(a) || a.render[0]?.suffix || a.kind;
      const label = `${a.sender === "human" ? "You" : a.sender}: ${String(txt).slice(0, 48)}`;
      window.dispatchEvent(
        new CustomEvent(REPLY_EVENT, {
          detail: { conversationId: a.conversationId, ref: a.id, label },
        }),
      );
      return;
    }
    if (action === "retry") {
      // a locally failed message → re-send it (same id → updates the SAME box in place:
      // fail → start → complete | fail). The channel keeps the message visible.
      const text = (a.render.find((rf) => rf.mode === "file")?.payload as { text?: string } | undefined)?.text
        || payloadText(a) || "";
      if (text) await ch.sendMessage(text, { id: a.id, conversationId: a.conversationId });
      return;
    }
    if (action === "prioritize" || action === "unprioritize") {
      const artifact = renderArtifactOf(a);
      await ch.prioritizeArtifact({
        conversationId: a.conversationId,
        artifact,
        artifactId: artifactMachineRefOf(artifact) || undefined,
        active: action === "prioritize",
        label: a.machine?.label || a.conversationId || "Conversation",
      });
      const trackableArtifact = payloadArtifactOf(a);
      if (hasArtifactMachine(trackableArtifact)) {
        void ch.trackArtifactInteraction({
          artifact: trackableArtifact,
          announcement: a,
          interaction: "@prioritized",
          active: action === "prioritize",
        }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
      return;
    }
    if (action === "download") {
      // Save the artifact that is ALREADY in the client (rendered/hydrated on
      // click) as a file — no refetch, and never the presigned S3 url (the
      // browser→S3 request is CORS-blocked). artifactToFile reads the in-memory
      // artifact + render payload (html / text / table / base64 file).
      const fileRender = a.render.find((rf) => rf.mode === "file") ?? a.render[0];
      const seed = renderArtifactOf(a) ?? payloadArtifactOf(a);
      // prefer the body already hydrated for display (interactive html / surface
      // table live in the renderer's cache, not the dehydrated announcement seed).
      let file = artifactToFile(ch.hydratedArtifactOf(seed) ?? seed, fileRender);
      if (!file && hasArtifactMachine(seed)) {
        // nothing in memory yet (never opened) — hydrate once, then save. Still no S3.
        const hydrated = await ch.hydrateArtifact(seed).catch(() => null);
        if (hydrated) file = artifactToFile(hydrated, fileRender);
      }
      if (file) {
        saveBlob(file.blob, file.filename);
        const artifact = payloadArtifactOf(a);
        if (hasArtifactMachine(artifact)) {
          void ch.trackArtifactInteraction({ artifact, announcement: a, interaction: "download" }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
        }
      }
      return;
    }
    // control is now an INLINE toggle inside each Box (header center) — no docked panel.
  };

  // LIFECYCLE CANCEL (control tab) — a HUMAN-session announce, never an
  // artifact bearer: the outlet detects sci//lifecycle:cancel on the ref lane,
  // retires the stored org-KMS secret first, then crosses the lifecycle
  // transition claim to world in the artifact's own conversation.
  const onCancelArtifact = async (a: Announcement) => {
    await ch.announce(artifactLifecycleCancelAnnounce(payloadArtifactOf(a) ?? renderArtifactOf(a)));
  };

  // LIFECYCLE SHARE (control tab) — a HUMAN-session announce granting the
  // artifact to a SUBJECT. The artifact stays in the scope it was created in;
  // sharing grants reach, it never moves the row. The subject is the scope the
  // human is looking at (their active org path), and `exclude` is the
  // first-class subtraction rather than the absence of a grant.
  const onShareArtifact = async (a: Announcement, mode: "with" | "exclude") => {
    if (!scopePath.startsWith("sci::org:") && !scopePath.startsWith("sci::human:")) {
      throw new Error(`artifact share needs a subject scope, got ${scopePath || "<none>"}`);
    }
    await ch.announce(artifactLifecycleShareAnnounce(
      payloadArtifactOf(a) ?? renderArtifactOf(a), [scopePath], mode));
  };

  const onArtifactInteraction = (
    interaction: ArtifactInteractionKind,
    a: Announcement,
    detail?: Record<string, unknown>,
  ): void | Promise<unknown> => {
    // A notify report @link → OPEN the linked conversation. The notification holds
    // no report, only links (the gate workspaces); the click resolves to the gate
    // workspace via the existing conversation-open path (ensureConversation +
    // activate) — no new transport.
    if (interaction === "open" && detail && typeof detail.conversationId === "string" && detail.conversationId) {
      const cid = detail.conversationId;
      workspace.setActiveConversationId(cid, typeof detail.label === "string" ? detail.label : undefined);
      workspace.setWorkspaceError("");
      // A failed context fetch surfaces as the workspace ERROR state — a
      // swallowed one read as an empty report workspace when the world
      // history read 500'd (the >6 MB context serve).
      // The report-link open is an interaction like any other — it claims its
      // evidence BEFORE routing (this was the one tracked-kind that returned
      // without its claim).
      const openedArtifact = payloadArtifactOf(a);
      if (hasArtifactMachine(openedArtifact)) {
        void ch.trackArtifactInteraction({ artifact: openedArtifact, announcement: a, interaction, detail }).catch((error) => console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error));
      }
      void ch.ensureConversation(cid).catch((error) => {
        workspace.setWorkspaceError(error instanceof Error ? error.message : String(error));
      });
      return;
    }
    const artifact = payloadArtifactOf(a);
    if (hasArtifactMachine(artifact)) {
      const reads = Array.isArray(detail?.reads)
        ? (detail?.reads as unknown[]).filter((atom): atom is string => typeof atom === "string" && atom.startsWith("sci"))
        : undefined;
      // The visible client owns the human session. A submitted map is a
      // separate privileged crossing; the artifact interaction remains its
      // own evidence/ref audit event and never transports map authority.
      if (interaction === "submitted" && detail?.map) {
        void ch.submitArtifactMap({ artifact, announcement: a, interaction, detail, reads }).catch((error) => console.error("[artifact-map] the privileged map claim failed (never reached the world):", error));
      }
      // THE TASK-QUEUE ADD AFFORDANCE: a submitted text from an artifact that
      // DECLARES a queue lane on its refs (sci//@org:task / sci//@sci:task —
      // the atom routes, never a machine-name sniff) rides the EXISTING
      // channel command path as a human message carrying the task ask INTO
      // the queue artifact's own conversation (the ONE routing owner). The
      // agent artifacts' re-assign/create intents ride the sibling owner —
      // the same announce path, mutually exclusive by detail shape.
      if (!routeTaskAskInteraction(ch.announce, a, artifact, interaction, detail)) {
        routeAgentIntent(ch.announce, a, artifact, interaction, detail);
      }
      // TWO DOORS, ONE CROSSING: the wrapper-owned harness pane's invoke input
      // declares door:"invoke" on its event detail — the shell transports the
      // declared word verbatim; the world's audit row records the door.
      const door = detail?.door === "invoke" ? "invoke" as const : undefined;
      // THE ACK IS THE HANDOFF (the cause-drill glass, 2026-08-08): the world
      // completes a reads-carrying recompose INSIDE the announce crossing, so
      // the resolved promise is the renderer's cue to pull the recomposed
      // body — returned, never swallowed (a drill that fired-and-forgot left
      // the artifact stale until some unrelated fold, the eternal
      // "Asking the book…").
      return ch.trackArtifactInteraction({ artifact, announcement: a, interaction, detail, reads, door }).then(
        () => true,
        (error) => {
          console.error("[artifact-interaction] claim failed (the interaction evidence never reached the world):", error);
          return false;
        },
      );
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", width: "100%" }}>
      {/* VIEW — the stream. Stays put while control docks beside it (not hidden). */}
      {/* Full width, MINIMAL x-padding — the artifacts use the whole page (no narrow
          centered column, no huge x-gutters). */}
      {/* bottom padding comes from .workspace-section-scroll (--command-clearance) */}
      <div ref={stream} className="workspace-section-scroll" style={{ paddingTop: 16, paddingInline: 16, minWidth: 0 }}>
        {scoped.length > 0 && (
          <div className="stream-filter-row" aria-label="Stream participant filters">
            {facets.map((facet) => {
              const active = workspace.participantFilter === facet.id;
              return (
                <button
                  key={facet.id}
                  type="button"
                  className="stream-filter"
                  data-active={active ? "true" : "false"}
                  style={toneVars(facet.tone, "conversation-filter")}
                  onClick={() => workspace.setParticipantFilter(active ? "" : facet.id)}
                  title={facet.label}
                >
                  <span className="stream-filter-icon" aria-hidden="true">
                    {facet.kind === "world" ? (
                      <img src="/logo/causit-tiny.svg" alt="" />
                    ) : facet.kind === "current" && clerkUser?.imageUrl ? (
                      <img src={clerkUser.imageUrl} alt="" />
                    ) : (
                      <UserIcon size={14} />
                    )}
                  </span>
                  <span>{facet.label}</span>
                </button>
              );
            })}
          </div>
        )}
        <div>
          {visible.length === 0 && (
            <div className="workspace-section-state" data-kind={conversationError ? "error" : conversationWaiting ? "loading" : "empty"}>
              {conversationError
                ? `Workspace unavailable. ${conversationError}`
                : conversationWaiting
                  ? "Loading workspace..."
                  : conversationId
                    ? "No announcements yet."
                    : "No conversation selected."}
            </div>
          )}
          {visible.map((a) => (
            <StreamBox
              key={a.id}
              a={a}
              onAction={onAction}
              onArtifactInteraction={onArtifactInteraction}
              accent={toneByFacet.get(primaryParticipantFacetId(a, currentIds))?.accent}
              hydrateArtifact={ch.hydrateArtifact}
              query={ch.query}
              onCancelArtifact={onCancelArtifact}
              onShareArtifact={onShareArtifact}
              trackVisibility={prioritizedArtifactIds.has(announcementArtifactMachineIdOf(a))}
            />
          ))}
          <div ref={bottom} />
        </div>
      </div>
    </div>
  );
};

const StreamBox: React.FC<{
  a: Announcement;
  onAction: (action: BoxAction, a: Announcement) => void;
  onArtifactInteraction: (interaction: ArtifactInteractionKind, a: Announcement, detail?: Record<string, unknown>) => void | Promise<unknown>;
  accent?: string;
  hydrateArtifact: ChannelApi["hydrateArtifact"];
  query: ChannelApi["query"];
  onCancelArtifact: (a: Announcement) => Promise<void>;
  onShareArtifact: (a: Announcement, mode: "with" | "exclude") => Promise<void>;
  trackVisibility: boolean;
}> = ({ a, onAction, onArtifactInteraction, accent, hydrateArtifact, query, onCancelArtifact, onShareArtifact, trackVisibility }) => {
  const workspace = useConversationWorkspace();
  const setStreamArtifactVisible = workspace.setStreamArtifactVisible;
  const ref = useRef<HTMLDivElement>(null);
  const artifactId = announcementArtifactMachineIdOf(a);

  useEffect(() => {
    const element = ref.current;
    if (!element || !artifactId || !trackVisibility) return;
    const sourceId = a.id;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setStreamArtifactVisible(artifactId, sourceId, entry.isIntersecting && entry.intersectionRatio > 0.02);
      },
      { threshold: [0, 0.02, 0.2] },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      setStreamArtifactVisible(artifactId, sourceId, false);
    };
  }, [a.id, artifactId, setStreamArtifactVisible, trackVisibility]);

  return (
    <div ref={ref} id={`box-${a.id}`} data-stream-artifact-id={artifactId}>
      <Box
        a={a}
        onAction={onAction}
        onArtifactInteraction={onArtifactInteraction}
        accent={accent}
        hydrateArtifact={hydrateArtifact}
        query={query}
        onCancelArtifact={onCancelArtifact}
        onShareArtifact={onShareArtifact}
      />
    </div>
  );
};
